<template>
    <div class="tracking-canvas-wrapper d-flex align-items-center justify-content-center">
      <!-- Main Core Centered Content Card -->
      <main class="tracking-display-card p-4 p-md-5">
        
        <!-- 👑 SUCCESS ANIMATION RADIAL GLYPH -->
        <header class="status-header-cluster text-center mb-4">
          <div class="radial-success-halo mx-auto mb-4 d-flex align-items-center justify-content-center">
            <div class="inner-success-circle d-flex align-items-center justify-content-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="3">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
          </div>
          
          <h1 class="main-status-title mb-2">Your order is on its way!</h1>
          <p class="status-subheadline text-muted mx-auto">
            Thank you for your purchase. We've received your order and are getting it ready.
          </p>
        </header>
  
        <!-- 📊 SECTION A: METADATA DETAILS BOX -->
        <section class="order-details-meta-box mb-4">
          <div class="meta-data-row d-flex justify-content-between align-items-center py-3 border-bottom">
            <span class="meta-property-label text-muted">ORDER NUMBER</span>
            <span class="meta-value-text font-weight-bold text-dark">#{{ orderInfo.orderNumber }}</span>
          </div>
          <div class="meta-data-row d-flex justify-content-between align-items-center py-3">
            <span class="meta-property-label text-muted">EST. DELIVERY</span>
            <span class="meta-value-text font-weight-bold text-primary-brand">{{ orderInfo.estDeliveryRange }}</span>
          </div>
        </section>
  
        <!-- 🚚 SECTION B: HORIZONTAL STEP TRACKER PROGRESSION BAR -->
        <section class="tracker-stepper-box p-3 mb-5">
          <div class="stepper-rail-line position-relative d-flex justify-content-between">
            
            <!-- Core Progress Indicator Fill Line -->
            <div class="progress-bar-fill-track position-absolute" :style="{ width: computedProgressWidth }"></div>
  
            <!-- Step 1: Placed -->
            <div class="step-node-item d-flex flex-column align-items-center position-relative">
              <div class="node-circle is-completed d-flex align-items-center justify-content-center">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="4">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <span class="node-label-text mt-2" :class="{ 'is-active-label': currentStep === 'placed' }">Placed</span>
            </div>
  
            <!-- Step 2: Processing -->
            <div class="step-node-item d-flex flex-column align-items-center position-relative">
              <div class="node-circle is-current-active d-flex align-items-center justify-content-center">
                <span class="active-inner-dot"></span>
              </div>
              <span class="node-label-text mt-2" :class="{ 'is-active-label': currentStep === 'processing' }">Processing</span>
            </div>
  
            <!-- Step 3: Shipped -->
            <div class="step-node-item d-flex flex-column align-items-center position-relative">
              <div class="node-circle is-pending"></div>
              <span class="node-label-text mt-2" :class="{ 'is-active-label': currentStep === 'shipped' }">Shipped</span>
            </div>
  
            <!-- Step 4: Delivered -->
            <div class="step-node-item d-flex flex-column align-items-center position-relative">
              <div class="node-circle is-pending"></div>
              <span class="node-label-text mt-2" :class="{ 'is-active-label': currentStep === 'delivered' }">Delivered</span>
            </div>
  
          </div>
        </section>
  
        <!-- 💳 ACTION ACCELERATOR PATHWAY BUTTONS -->
        <footer class="tracking-actions-dock d-flex flex-column gap-3">
          <button 
            class="track-order-primary-btn d-flex align-items-center justify-content-center gap-2"
            @click="dispatchExternalTrackingPipeline"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <rect x="1" y="3" width="15" height="13" rx="2" ry="2"></rect>
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
              <circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle>
            </svg>  
            <span style="padding-left: 10px;">Track Order</span>
          </button>
  
          <button class="back-home-secondary-btn" @click="routeBackToDashboard">
            Back to Home
          </button>
        </footer>
  
      </main>
    </div>
  </template>
  
  <script>
  // @ts-nocheck
  import Vue from 'vue';
  
  export default Vue.extend({
    name: 'Tracking',
    data() {
      return {
        currentStep: 'processing', // Steps mapping choices: 'placed', 'processing', 'shipped', 'delivered'
        
        orderInfo: {
          orderNumber: 'SH-882910',
          estDeliveryRange: 'Oct 24 - Oct 26'
        }
      };
    },
    computed: {
      computedProgressWidth() {
        // Maps horizontal calculation constraints natively based on step selection tokens
        if (this.currentStep === 'placed') return '0%';
        if (this.currentStep === 'processing') return '33.33%';
        if (this.currentStep === 'shipped') return '66.66%';
        if (this.currentStep === 'delivered') return '100%';
        return '0%';
      }
    },
    methods: {
      dispatchExternalTrackingPipeline() {
        console.log(`[Tracking Action]: Querying live satellite logistics stream for: ${this.orderInfo.orderNumber}`);
        this.$emit('track-order', this.orderInfo.orderNumber);
      },
      routeBackToDashboard() {
        console.log('[Navigation]: Returning user view context back to root landing canvas.');
        this.$emit('go-home');
      }
    }
  });
  </script>


<style lang="scss" scoped>
$font-sans: 'Public Sans', system-ui, -apple-system, sans-serif;
$color-brand-blue: #0046e8; // Vibrant royal blue core highlight token matching mockup design
$color-bg-base: #f8fafc;
$color-border-line: #cbd5e1;
$color-dark-main: #0c1a30;
$color-muted-main: #64748b;

.tracking-canvas-wrapper {
  font-family: $font-sans;
  background-color: #fafbfc;
  color: $color-dark-main;
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;
}

/* 🏛️ STRUCTURAL CONTAINER DISPLAY CARD */
.tracking-display-card {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  width: 100%;
  max-width: 500px; // Standard production mobile profile enclosure bounding box
  box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.02);
  box-sizing: border-box;
}

/* 👑 SUCCESS ANIMATION RADIAL HEADERS */
.radial-success-halo {
  width: 110px;
  height: 110px;
  background-color: #eff6ff; // Exterior primary light halo outline shading ring
  border-radius: 50%;
  animation: pulseHalo 2.5s infinite ease-in-out;
}

.inner-success-circle {
  width: 82px;
  height: 82px;
  background-color: $color-brand-blue; // Dense solid inner center core
  border-radius: 50%;
  box-shadow: 0 8px 16px rgba(0, 70, 232, 0.2);
}

.main-status-title {
  font-size: 1.65rem;
  font-weight: 800;
  color: $color-dark-main;
  letter-spacing: -0.02em;
}

.status-subheadline {
  font-size: 0.95rem;
  line-height: 1.45;
  max-width: 360px;
}

/* 📊 ORDER DETAILS META STRIP CARD */
.order-details-meta-box {
  background-color: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  padding: 0 1rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.01);

  .meta-property-label {
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.05em;
  }

  .meta-value-text {
    font-size: 1rem;
  }

  .text-primary-brand {
    color: $color-brand-blue;
    font-weight: 800;
  }
}

/* 🚚 STEPPER PROGRESSION TRAIL DESIGN LAYOUTS */
.tracker-stepper-box {
  background-color: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  box-sizing: border-box;
}

.stepper-rail-line {
  &::before {
    // Structural static gray background connection track line layout
    content: '';
    position: absolute;
    top: 14px;
    left: 0;
    right: 0;
    height: 3px;
    background-color: #e2e8f0;
    z-index: 1;
  }
}

.progress-bar-fill-track {
  top: 14px;
  left: 0;
  height: 3px;
  background-color: $color-brand-blue; // Active scaling filled track segment line
  z-index: 2;
  transition: width 0.4s ease-in-out;
}

.step-node-item {
  z-index: 3;
  width: 70px; // Rigid label container prevents fluid text wrapping overlap breaks
}

.node-circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #ffffff;
  border: 3px solid #cbd5e1;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &.is-completed {
    background-color: $color-brand-blue;
    border-color: $color-brand-blue;
  }

  &.is-current-active {
    border-color: $color-brand-blue;
    border-width: 3.5px;
    background-color: #ffffff;
    box-shadow: 0 0 0 5px rgba(0, 70, 232, 0.15); // Pulsing ring track focus halo ring
  }

  &.is-pending {
    background-color: #e2e8f0;
    border-color: #e2e8f0;
  }
}

.active-inner-dot {
  width: 10px;
  height: 10px;
  background-color: $color-brand-blue;
  border-radius: 50%;
  display: block;
}

.node-label-text {
  font-size: 0.75rem;
  font-weight: 600;
  color: $color-muted-main;
  transition: color 0.2s ease;
  text-align: center;

  &.is-active-label {
    color: $color-brand-blue;
    font-weight: 800;
  }
}

/* 💳 ACTION TERMINAL MANAGEMENT BUTTONS */
.track-order-primary-btn {
  background-color: $color-brand-blue;
  color: #ffffff;
  border: none;
  height: 50px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 70, 232, 0.15);
  transition: background-color 0.2s ease;

  &:hover {
    background-color: darken($color-brand-blue, 5%);
  }
}

.back-home-secondary-btn {
  background-color: #f8fafc;
  color: $color-brand-blue;
  border: 1px solid #e2e8f0;
  height: 50px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 15px;

  &:hover {
    background-color: #edf2f7;
    border-color: #cbd5e1;
  }
}

/* SUCCESS HALO OVERLAY KEYFRAMES PULSE PULSE */
@keyframes pulseHalo {
  0%, 100% {
    transform: scale(0.97);
    box-shadow: 0 0 0 0px rgba(0, 70, 232, 0.05);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 0 0 12px rgba(0, 70, 232, 0.02);
  }
}

/* ─── 📱 DESKTOP COMFORT WIDTH OVERRIDES ─── */
@media (min-width: 768px) {
  .main-status-title {
    font-size: 2rem;
  }
  
  .status-subheadline {
    font-size: 1.05rem;
  }
}
</style>

  